// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import borderByState, { sizeVariantValues } from "@insite/mobius/FormField/formStyles";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import get from "@insite/mobius/utilities/get";

/**
 * Provides a config object that describes the styles of the form field in accordance with the props passed.
 * @param {object} props The Props object of the form component.
 * @param {object} props.theme The site's theme object.
 * @param {object} [props.theme.typography.body.fontFamily] Base font family on the site.
 * @param {string} [props.theme.colors.text.main] Base text color for the site.
 * @param {string} [props.theme.formField.defaultProps.border="underline"] Default FormField border.
 * @param {string} [props.theme.formField.defaultProps.sizeVariant="default"] Default FormField size variant.
 * @param {object} [props.theme.formField.defaultProps.cssOverrides] Default FormField cssOverrides.
 * @param {string} [props.theme.tokenExFrame.defaultProps.border] Default TokenExFrame border.
 * @param {string} [props.theme.tokenExFrame.defaultProps.size] Default TokenExFrame size.
 * @param {object} [props.theme.tokenExFrame.defaultProps.cssOverrides] Default TokenExFrame cssOverrides.
 * @param {string} [props.sizeVariant] The form element's sizeVariant.
 * @param {string} [props.border] The component's border style.
 * @param {object} [props.cssOverrides] The component's css overrides.
 * @return {object} config object describing TokenEx style settings.
 */
export const generateTokenExFrameStyleConfig = (props: {
    theme: BaseTheme;
    sizeVariant?: "default" | "small";
    border?: "underline" | "rectangle" | "rounded";
    cssOverrides?: object;
}) => {
    const { theme } = props;
    const { applyProp, spreadProps } = applyPropBuilder(props, {
        component: "textField",
        category: "formField",
    });
    const border = applyProp("border", "underline");
    const sizeVariant = applyProp("sizeVariant" as any, "default") as keyof typeof sizeVariantValues;
    const cssOverrides = spreadProps("cssOverrides");
    const bluePrint = BLUEPRINT_NAME.toLowerCase();
    let baseRaw = "";
    let focusRaw = "";
    let errorRaw = "";

    if (bluePrint === "bg") {
        baseRaw =
            `height: 40px;    ` +
            "width: 100%;    " +
            "box-sizing: border-box;    " +
            "padding: 0 10px 0 10px;    " +
            "border: 1px solid; " +
            "border-radius: 4px; " +
            `font-size: 16px;    ` +
            `outline: 0;    ` +
            `appearance: none;    ` +
            `color: ${get(theme, "colors.text.main")};  ` +
            `border-color: ${get(theme, "colors.common.ui4")};  ` +
            `background: ${get(theme, "colors.common.ui4")};  `;

        focusRaw = `    
        border: 1px solid ${get(theme, "colors.secondary.main")};
        border-radius: 4px;
        padding: 0 10px 0 10px;
        `;

        errorRaw = `    
        border: 1px solid ${get(theme, "colors.danger.main")};
        border-radius: 4px;
        border-left-width: 4px;
        padding: 0 10px 0 10px;`;
    } else if (bluePrint === "bgs") {
        baseRaw =
            `height: 36px;    ` +
            "width: 100%;    " +
            "box-sizing: border-box;    " +
            "padding:  0 10px 0 10px;    " +
            "border: 1px solid; " +
            "border-radius: 0px; " +
            `font-size: 16px;    ` +
            `outline: 0;    ` +
            `appearance: none;    ` +
            `color: ${get(theme, "colors.text.main")};  ` +
            `border-color: ${get(theme, "colors.text.main")};  `;

        focusRaw = `    
        border: 2px solid ${get(theme, "colors.text.main")};
        border-radius: 0px;
        padding: 0 9px 0 9px;
        background: ${get(theme, "colors.common.ui4")};`;

        errorRaw = `    
        border: 2px solid ${get(theme, "colors.danger.main")};
        border-radius: 0px;
        padding: 0 10px 0 10px;`;
    } else {
        baseRaw =
            `height: 40px;    ` +
            "width: 100%;    " +
            "box-sizing: border-box;    " +
            "padding: 0 14px 0 14px;    " +
            "border: 1px solid; " +
            "border-radius: 0px; " +
            `font-size: 16px;    ` +
            `outline: 0;    ` +
            `appearance: none;    ` +
            `color: ${get(theme, "colors.text.main")};  ` +
            `border-color: ${get(theme, "colors.common.ui2")};  `;

        focusRaw = `    
        border: 1px solid ${get(theme, "colors.text.main")};
        border-radius: 0px;
        box-shadow: 0 0 4px 0 rgb(51 49 50 / 60%);
        padding: 0 13px 0 13px;`;

        errorRaw = `    
        border: 1px solid ${get(theme, "colors.text.main")};
        border-left-color: ${get(theme, "colors.danger.main")};
        border-radius: 0px;
        border-left-width: 5px;
        box-shadow: none;
        padding: 0 14px 0 14px;`;
    }
    const base = baseRaw.replace(/(\r\n|\n|\r)/, "");
    const focus = focusRaw.replace(/(\r\n|\n|\r)/, "");
    const error = errorRaw.replace(/(\r\n|\n|\r)/, "");
    return {
        base,
        focus,
        error,
        cvv: {
            base,
            focus,
            error,
        },
    };
};

export const sizeVariants: { [key: string]: number } = {
    bg: 40,
    bgs: 36,
    bp: 40,
};
